import { useEffect, useState, useCallback } from "react";
import { api, formatNumber, progressColor, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Loader2, Plus, Trash2, Layers3, Ruler, Pencil, Check, X } from "lucide-react";
import { toast } from "sonner";

// The 4 stages
const STAGE_DEFS = [
  { key: "kazi", label: "Kazı", short: "K" },
  { key: "radye", label: "Radye", short: "R" },
  { key: "perde", label: "Perde", short: "P" },
  { key: "tabliye", label: "Tabliye", short: "T", closedOnly: true },
];

function anoStagesInfo(ano) {
  const total = ano.type === "kapali" ? 4 : 3;
  const done = STAGE_DEFS.reduce((acc, s) => {
    if (s.closedOnly && ano.type !== "kapali") return acc;
    return acc + (ano.stages?.[s.key] ? 1 : 0);
  }, 0);
  return { done, total, pct: total > 0 ? (done / total) * 100 : 0 };
}

// Compute per-stage metraj (m) breakdown for a list of anos.
// planned = total length of anos where the stage APPLIES.
// done = total length of anos where the stage is marked done.
function computeStageMetrics(anoList) {
  return STAGE_DEFS.map((s) => {
    const applicable = anoList.filter((a) => !s.closedOnly || a.type === "kapali");
    const planned = applicable.reduce((sum, a) => sum + (parseFloat(a.length) || 0), 0);
    const done = applicable.reduce(
      (sum, a) => sum + (a.stages?.[s.key] ? parseFloat(a.length) || 0 : 0),
      0
    );
    const remaining = Math.max(planned - done, 0);
    const pct = planned > 0 ? (done / planned) * 100 : 0;
    return { key: s.key, label: s.label, planned, done, remaining, pct, applicable: applicable.length };
  }).filter((sm) => sm.applicable > 0); // hide stages that apply to no ano (e.g., Tabliye when no kapalı)
}

// Stage color mapping for consistent visual identity
const STAGE_COLORS = {
  kazi: { bg: "bg-amber-50", border: "border-amber-300", text: "text-amber-800", dot: "bg-amber-500" },
  radye: { bg: "bg-blue-50", border: "border-blue-300", text: "text-blue-800", dot: "bg-blue-500" },
  perde: { bg: "bg-purple-50", border: "border-purple-300", text: "text-purple-800", dot: "bg-purple-500" },
  tabliye: { bg: "bg-teal-50", border: "border-teal-300", text: "text-teal-800", dot: "bg-teal-500" },
};

function StageMetricCard({ metric, pursantaj = null, testIdSuffix = "" }) {
  const c = STAGE_COLORS[metric.key] || STAGE_COLORS.kazi;
  return (
    <div
      className={`${c.bg} ${c.border} border rounded-md p-3 flex flex-col gap-1.5`}
      data-testid={`stage-metric-${metric.key}${testIdSuffix ? "-" + testIdSuffix : ""}`}
    >
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-1.5">
          <span className={`w-2 h-2 rounded-full ${c.dot}`} />
          <span className={`text-[10px] font-black uppercase tracking-widest ${c.text}`}>{metric.label}</span>
          {pursantaj != null && pursantaj > 0 && (
            <span className="text-[9px] font-black uppercase tracking-widest bg-white/70 text-slate-700 border border-slate-200 px-1.5 rounded-full font-mono-num" title="Aşama pursantajı">
              %{pursantaj.toFixed(1)}
            </span>
          )}
        </div>
        <span className="font-mono-num font-black text-sm" style={{ color: progressColor(metric.pct) }}>
          %{metric.pct.toFixed(1)}
        </span>
      </div>
      <div className="grid grid-cols-3 gap-1 text-[10px]">
        <div>
          <div className="text-slate-500 font-bold uppercase tracking-wider">Plan</div>
          <div className="font-mono-num font-bold text-slate-900">{formatNumber(metric.planned)} m</div>
        </div>
        <div>
          <div className="text-slate-500 font-bold uppercase tracking-wider">Yapılan</div>
          <div className="font-mono-num font-bold text-green-700">{formatNumber(metric.done)} m</div>
        </div>
        <div>
          <div className="text-slate-500 font-bold uppercase tracking-wider">Kalan</div>
          <div className="font-mono-num font-bold text-sky-700">{formatNumber(metric.remaining)} m</div>
        </div>
      </div>
      <div className="w-full h-1 bg-white rounded-full overflow-hidden">
        <div
          style={{ width: `${Math.min(metric.pct, 100)}%`, backgroundColor: progressColor(metric.pct) }}
          className="h-full"
        />
      </div>
    </div>
  );
}

function StageMetricsGrid({ anos, stagePursantaj = null, testIdSuffix = "", label = "Aşama Bazlı Metraj" }) {
  const metrics = computeStageMetrics(anos);
  if (metrics.length === 0) return null;
  return (
    <div className="space-y-2" data-testid={`stage-metrics-grid${testIdSuffix ? "-" + testIdSuffix : ""}`}>
      <div className="text-[11px] font-black uppercase tracking-widest text-slate-600">{label}</div>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-2">
        {metrics.map((m) => (
          <StageMetricCard
            key={m.key}
            metric={m}
            pursantaj={stagePursantaj ? (stagePursantaj[m.key] ?? null) : null}
            testIdSuffix={testIdSuffix}
          />
        ))}
      </div>
    </div>
  );
}

// 3-state color: 0 done → red, all done → green, in between → yellow
function anoBadge(pct) {
  if (pct >= 100) return { border: "border-l-green-500", bg: "bg-green-50", label: "Tamamlandı", text: "text-green-800" };
  if (pct > 0) return { border: "border-l-amber-500", bg: "bg-amber-50/50", label: "Devam Ediyor", text: "text-amber-800" };
  return { border: "border-l-red-500", bg: "bg-red-50/40", label: "Başlanmadı", text: "text-red-800" };
}

function StagePill({ label, done, disabled, onToggle, canEdit }) {
  return (
    <button
      type="button"
      onClick={canEdit ? onToggle : undefined}
      disabled={!canEdit || disabled}
      className={`px-2.5 py-1 rounded-md text-[11px] font-bold border-2 transition-all whitespace-nowrap flex items-center gap-1
        ${disabled ? "bg-slate-100 text-slate-400 border-slate-200 cursor-not-allowed" : ""}
        ${!disabled && done ? "bg-green-100 text-green-800 border-green-400 hover:bg-green-200" : ""}
        ${!disabled && !done ? "bg-red-50 text-red-700 border-red-300 hover:bg-red-100" : ""}
        ${canEdit && !disabled ? "cursor-pointer active:scale-95" : ""}
      `}
      data-testid={`stage-${label.toLowerCase()}${done ? "-done" : ""}`}
      title={disabled ? "Sadece Kapalı tip için" : done ? "Yapıldı — kaldırmak için tıkla" : "Yapılmadı — işaretlemek için tıkla"}
    >
      {done ? <Check size={12} /> : <X size={12} className="opacity-60" />}
      {label}
    </button>
  );
}

function AnoRow({ ano, canEdit, onOptimisticUpdate, onStructureChange, onDelete }) {
  const info = anoStagesInfo(ano);
  const badge = anoBadge(info.pct);
  const [editing, setEditing] = useState(false);
  const [editType, setEditType] = useState(ano.type);
  const [editLength, setEditLength] = useState(String(ano.length));
  const [saving, setSaving] = useState(false);

  const toggleStage = async (stageKey) => {
    if (!canEdit) return;
    const prevStages = ano.stages;
    const newStages = { ...ano.stages, [stageKey]: !ano.stages[stageKey] };
    // Optimistic UI: update local state immediately (no spinner, no scroll jump)
    onOptimisticUpdate(ano.id, { stages: newStages });
    try {
      await api.put(`/dere/anos/${ano.id}`, { stages: newStages });
      // Header % refresh in background (with scroll preservation)
      onStructureChange();
    } catch (err) {
      toast.error(formatApiError(err));
      // Revert optimistic update on failure
      onOptimisticUpdate(ano.id, { stages: prevStages });
    }
  };

  const saveMeta = async () => {
    const len = parseFloat(editLength);
    if (!editType || isNaN(len) || len <= 0) { toast.error("Geçerli tip ve uzunluk gerekli"); return; }
    setSaving(true);
    try {
      await api.put(`/dere/anos/${ano.id}`, { type: editType, length: len });
      toast.success("Ano güncellendi");
      setEditing(false);
      // Optimistic update for type/length too
      onOptimisticUpdate(ano.id, { type: editType, length: len });
      onStructureChange();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally { setSaving(false); }
  };

  return (
    <div className={`border-l-4 ${badge.border} ${badge.bg} border border-slate-200 rounded-md p-3 flex flex-col sm:flex-row sm:items-center gap-3`} data-testid={`ano-row-${ano.id}`}>
      {/* Number + type badge */}
      <div className="flex items-center gap-3 min-w-[180px]">
        <div className="w-11 h-11 rounded-md flex items-center justify-center bg-sky-500 text-white font-black text-sm font-mono-num flex-shrink-0" title={`Ano ${ano.number}`}>
          #{ano.number}
        </div>
        {editing ? (
          <div className="flex flex-col gap-1">
            <Select value={editType} onValueChange={setEditType}>
              <SelectTrigger className="h-8 text-xs border-2 border-sky-300 w-28"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="u_kesit">U Kesit</SelectItem>
                <SelectItem value="kapali">Kapalı</SelectItem>
              </SelectContent>
            </Select>
            <div className="flex items-center gap-1">
              <Input type="number" step="0.01" min="0.01" value={editLength} onChange={(e) => setEditLength(e.target.value)}
                className="h-8 text-xs w-20 font-mono-num border-2 border-sky-300" />
              <span className="text-[10px] text-slate-500 font-mono-num">m</span>
            </div>
          </div>
        ) : (
          <div>
            <span className={`inline-block text-[10px] font-black uppercase tracking-widest px-2 py-0.5 rounded-full ${ano.type === "kapali" ? "bg-sky-100 text-sky-800 border border-sky-300" : "bg-slate-100 text-slate-700 border border-slate-300"}`}>
              {ano.type === "kapali" ? "Kapalı" : "U Kesit"}
            </span>
            <div className="mt-1 flex items-center gap-1 text-xs text-slate-600">
              <Ruler size={11} className="text-slate-400" />
              <span className="font-mono-num font-bold">{formatNumber(ano.length)} m</span>
            </div>
          </div>
        )}
      </div>

      {/* Stage toggles */}
      <div className="flex flex-wrap items-center gap-1.5 flex-1">
        {STAGE_DEFS.map((s) => (
          <StagePill
            key={s.key}
            label={s.label}
            done={!!ano.stages?.[s.key]}
            disabled={s.closedOnly && ano.type !== "kapali"}
            onToggle={() => toggleStage(s.key)}
            canEdit={canEdit}
          />
        ))}
      </div>

      {/* Progress + actions */}
      <div className="flex items-center gap-3 sm:min-w-[220px] justify-end">
        <div className="text-right">
          <div className="text-[9px] font-bold uppercase tracking-widest text-slate-500">İlerleme</div>
          <div className="font-mono-num font-black text-lg" style={{ color: progressColor(info.pct) }}>
            {info.done}/{info.total} · %{info.pct.toFixed(0)}
          </div>
          <div className={`text-[9px] font-bold uppercase tracking-widest ${badge.text}`}>{badge.label}</div>
        </div>
        {canEdit && (
          <div className="flex items-center gap-0.5">
            {editing ? (
              <>
                <button onClick={saveMeta} disabled={saving} className="p-1.5 text-green-700 hover:bg-green-50 rounded" title="Kaydet" data-testid={`ano-save-${ano.id}`}>
                  {saving ? <Loader2 className="animate-spin" size={13} /> : <Check size={13} />}
                </button>
                <button onClick={() => { setEditing(false); setEditType(ano.type); setEditLength(String(ano.length)); }} className="p-1.5 text-red-700 hover:bg-red-50 rounded" title="İptal">
                  <X size={13} />
                </button>
              </>
            ) : (
              <>
                <button onClick={() => setEditing(true)} className="p-1.5 text-slate-500 hover:text-sky-600 hover:bg-sky-50 rounded" title="Tip/Uzunluk düzenle" data-testid={`ano-edit-${ano.id}`}>
                  <Pencil size={13} />
                </button>
                <button onClick={() => onDelete(ano)} className="p-1.5 text-red-600 hover:bg-red-50 rounded" title="Ano'yu sil" data-testid={`ano-delete-${ano.id}`}>
                  <Trash2 size={13} />
                </button>
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

function AddAnoDialog({ open, onClose, itemKey, groupKey, groupName, onSaved }) {
  const [count, setCount] = useState("1");
  const [type, setType] = useState("kapali");
  const [length, setLength] = useState("10");
  const [saving, setSaving] = useState(false);

  useEffect(() => { if (open) { setCount("1"); setType("kapali"); setLength("10"); } }, [open]);

  const submit = async () => {
    const c = parseInt(count, 10);
    const l = parseFloat(length);
    if (!c || c < 1 || c > 500) { toast.error("Ano sayısı 1-500 arası olmalı"); return; }
    if (!l || l <= 0) { toast.error("Geçerli uzunluk girin"); return; }
    setSaving(true);
    try {
      const { data } = await api.post("/dere/anos", { item_key: itemKey, group_key: groupKey, count: c, type, length: l });
      toast.success(`${data.created} adet ano oluşturuldu`);
      onSaved();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="add-ano-dialog">
        <DialogHeader>
          <DialogTitle className="text-xl font-black font-display">Ano Ekle</DialogTitle>
          <DialogDescription>
            <span className="font-bold text-slate-700">{groupName}</span> derenin altına ano ekleyin. Aynı tip ve uzunluğa sahip birden fazla ano tek seferde oluşturulur. Farklı özellikte olanlar için sonradan tek tek düzenlenebilir.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4 pt-2">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="font-semibold text-sm text-slate-700">Ano Sayısı *</Label>
              <Input type="number" min="1" max="500" value={count} onChange={(e) => setCount(e.target.value)}
                className="mt-1.5 h-11 border-2 border-slate-300 font-mono-num" data-testid="ano-count-input" autoFocus />
            </div>
            <div>
              <Label className="font-semibold text-sm text-slate-700">Tip *</Label>
              <Select value={type} onValueChange={setType}>
                <SelectTrigger className="mt-1.5 h-11 border-2 border-slate-300" data-testid="ano-type-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="u_kesit">U Kesit (3 aşama: Kazı, Radye, Perde)</SelectItem>
                  <SelectItem value="kapali">Kapalı (4 aşama: Kazı, Radye, Perde, Tabliye)</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <div>
            <Label className="font-semibold text-sm text-slate-700">Ano Uzunluğu (m) *</Label>
            <Input type="number" step="0.01" min="0.01" value={length} onChange={(e) => setLength(e.target.value)}
              className="mt-1.5 h-11 border-2 border-slate-300 font-mono-num" data-testid="ano-length-input" placeholder="Örn: 12.5" />
            <div className="text-xs text-slate-500 mt-1">Her ano için aynı uzunluk uygulanır. Sonradan tek tek değiştirebilirsiniz.</div>
          </div>
        </div>
        <DialogFooter className="mt-4">
          <Button variant="outline" onClick={onClose}>İptal</Button>
          <Button className="bg-sky-500 hover:bg-sky-600 text-white font-bold" onClick={submit} disabled={saving} data-testid="ano-save-bulk">
            {saving ? <Loader2 className="animate-spin" size={16} /> : "Ekle"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function DereAnoView({ item, canEdit, isAdmin, onGroupAdd, onGroupEdit, onGroupDelete, onChanged }) {
  const [anos, setAnos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [addingFor, setAddingFor] = useState(null);
  const [deletingAno, setDeletingAno] = useState(null);

  const loadAnos = useCallback(async ({ silent = false } = {}) => {
    if (!silent) setLoading(true);
    try {
      const { data } = await api.get("/dere/anos", { params: { item_key: item.key } });
      setAnos(data);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally { if (!silent) setLoading(false); }
  }, [item.key]);

  useEffect(() => { loadAnos(); }, [loadAnos]);

  // Optimistic patch of a single ano (avoids spinner + scroll jump on frequent toggles)
  const applyOptimisticUpdate = useCallback((anoId, patch) => {
    setAnos((prev) => prev.map((a) => {
      if (a.id !== anoId) return a;
      const merged = { ...a, ...patch };
      // Recompute progress info if stages/type changed
      const info = anoStagesInfo(merged);
      return { ...merged, progress: parseFloat(info.pct.toFixed(2)), done_stages: info.done, total_stages: info.total };
    }));
  }, []);

  const groups = item.groups || [];

  const doDelete = async () => {
    if (!deletingAno) return;
    try {
      await api.delete(`/dere/anos/${deletingAno.id}`);
      toast.success("Ano silindi");
      setDeletingAno(null);
      loadAnos({ silent: true });
      onChanged?.();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  return (
    <div className="site-card p-6 rounded-lg" data-testid="dere-ano-view">
      <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
        <div>
          <h2 className="text-lg font-black text-slate-900 uppercase tracking-tight font-display flex items-center gap-2">
            <Layers3 size={20} className="text-sky-600" /> {item.key === "dere" ? "Dereler" : "Bölümler"} ({groups.length})
          </h2>
          <p className="text-xs text-slate-500 mt-1">
            Her {item.key === "dere" ? "derenin" : "bölümün"} altına anolar eklenir. Ano tipi (U/Kapalı) ve uzunluğu ayarlanır; aşama düğmelerine tıklayarak durum değiştirilir.
          </p>
        </div>
        {isAdmin && (
          <Button className="bg-sky-500 hover:bg-sky-600 text-white h-10 font-bold" onClick={onGroupAdd} data-testid="add-dere-button">
            <Plus size={14} className="mr-1.5" /> Yeni {item.key === "dere" ? "Dere" : "Bölüm"}
          </Button>
        )}
      </div>

      {/* Overall stage metrics across all groups */}
      {groups.length > 0 && anos.length > 0 && (
        <div className="mb-5 p-4 rounded-md bg-gradient-to-br from-slate-50 to-white border border-slate-200">
          <StageMetricsGrid
            anos={anos}
            stagePursantaj={item.stage_pursantaj || null}
            testIdSuffix="overall"
            label={`Tüm ${item.key === "dere" ? "Dereler" : "Bölümler"} · Aşama Bazlı Metraj`}
          />
        </div>
      )}

      {groups.length === 0 ? (
        <div className="text-center py-12 border-2 border-dashed border-slate-200 rounded-md">
          <div className="text-slate-500 mb-3">Henüz {item.key === "dere" ? "dere" : "bölüm"} tanımlanmamış.</div>
          {isAdmin && (
            <Button className="bg-sky-500 hover:bg-sky-600 text-white font-bold" onClick={onGroupAdd}>
              <Plus size={14} className="mr-1.5" /> İlk {item.key === "dere" ? "Dereyi" : "Bölümü"} Ekle
            </Button>
          )}
        </div>
      ) : (
        <Accordion type="multiple" className="space-y-2">
          {groups.map((g) => {
            const groupAnos = anos.filter((a) => a.group_key === g.key);
            const gTotalLen = groupAnos.reduce((s, a) => s + a.length, 0);
            const gDoneLen = groupAnos.reduce((s, a) => s + a.length * (anoStagesInfo(a).pct / 100), 0);
            const gPct = gTotalLen > 0 ? (gDoneLen / gTotalLen) * 100 : 0;
            return (
              <AccordionItem key={g.key} value={g.key} className="border border-slate-200 rounded-lg" data-testid={`dere-group-${g.key}`}>
                <AccordionTrigger className="px-3 sm:px-4 py-3 hover:no-underline">
                  <div className="flex items-center gap-2 sm:gap-4 flex-1 min-w-0">
                    <div className="w-8 h-8 sm:w-9 sm:h-9 rounded-md flex items-center justify-center bg-sky-100 text-sky-700 flex-shrink-0">
                      <Layers3 size={14} />
                    </div>
                    <div className="flex-1 text-left min-w-0">
                      <div className="font-black text-slate-900 truncate text-sm sm:text-base">{g.name}</div>
                      <div className="text-[11px] sm:text-xs text-slate-500 mt-0.5 flex items-center gap-1.5 sm:gap-2 flex-wrap">
                        <span>{groupAnos.length} ano</span>
                        {gTotalLen > 0 && (
                          <>
                            <span className="hidden sm:inline">·</span>
                            <span className="font-mono-num">
                              <span className="hidden sm:inline">Yapılan </span>
                              <span className="font-bold text-green-700">{formatNumber(gDoneLen)}m</span>
                              <span className="text-slate-400 mx-1">/</span>
                              <span className="font-bold text-slate-700">{formatNumber(gTotalLen)}m</span>
                            </span>
                          </>
                        )}
                      </div>
                    </div>
                    <div className="text-right pr-1 sm:pr-2 flex-shrink-0">
                      <div className="text-[9px] sm:text-[10px] font-bold uppercase tracking-widest text-slate-500 hidden sm:block">İlerleme</div>
                      <div className="font-mono-num font-black text-base sm:text-lg" style={{ color: progressColor(gPct) }}>%{gPct.toFixed(1)}</div>
                    </div>
                    {isAdmin && (
                      <div className="hidden sm:flex items-center gap-0.5 mr-2 flex-shrink-0">
                        <button type="button" onClick={(e) => { e.stopPropagation(); onGroupEdit(g); }} className="p-1.5 text-slate-500 hover:text-sky-600 hover:bg-sky-50 rounded" title="Yeniden adlandır" data-testid={`edit-dere-${g.key}`}>
                          <Pencil size={13} />
                        </button>
                        <button type="button" onClick={(e) => { e.stopPropagation(); onGroupDelete(g); }} className="p-1.5 text-red-600 hover:bg-red-50 rounded" title="Dereyi sil" data-testid={`delete-dere-${g.key}`}>
                          <Trash2 size={13} />
                        </button>
                      </div>
                    )}
                  </div>
                </AccordionTrigger>
                <AccordionContent className="px-4 pb-4">
                  <div className="pt-2 border-t border-slate-100 space-y-3">
                    {canEdit && (
                      <div className="flex justify-end mb-2">
                        <Button size="sm" className="bg-sky-500 hover:bg-sky-600 text-white font-bold h-9" onClick={() => setAddingFor(g)} data-testid={`add-ano-btn-${g.key}`}>
                          <Plus size={13} className="mr-1" /> Ano Ekle
                        </Button>
                      </div>
                    )}
                    {/* Per-group stage-based metraj breakdown */}
                    {groupAnos.length > 0 && (
                      <StageMetricsGrid
                        anos={groupAnos}
                        stagePursantaj={item.stage_pursantaj || null}
                        testIdSuffix={g.key}
                        label={`${g.name} · Aşama Bazlı Metraj`}
                      />
                    )}
                    {loading ? (
                      <div className="flex justify-center py-6"><Loader2 className="animate-spin text-sky-500" size={24} /></div>
                    ) : groupAnos.length === 0 ? (
                      <div className="text-center py-8 text-sm text-slate-500 bg-slate-50 rounded-md">
                        Bu derenin altında henüz ano yok. {canEdit && "Yukarıdaki \"Ano Ekle\" butonu ile başlayın."}
                      </div>
                    ) : (
                      <div className="space-y-2">
                        {groupAnos.map((ano) => (
                          <AnoRow
                            key={ano.id}
                            ano={ano}
                            canEdit={canEdit}
                            onOptimisticUpdate={applyOptimisticUpdate}
                            onStructureChange={() => { loadAnos({ silent: true }); onChanged?.(); }}
                            onDelete={setDeletingAno}
                          />
                        ))}
                        <div className="text-xs text-slate-500 pt-2 flex items-center justify-end gap-4 border-t border-slate-100">
                          <span>Toplam Uzunluk: <span className="font-mono-num font-bold text-slate-900">{formatNumber(gTotalLen)} m</span></span>
                          <span>Tamamlanan: <span className="font-mono-num font-bold text-green-700">{formatNumber(gDoneLen)} m</span></span>
                        </div>
                      </div>
                    )}
                  </div>
                </AccordionContent>
              </AccordionItem>
            );
          })}
        </Accordion>
      )}

      <AddAnoDialog
        open={!!addingFor}
        onClose={() => setAddingFor(null)}
        itemKey={item.key}
        groupKey={addingFor?.key}
        groupName={addingFor?.name}
        onSaved={() => { setAddingFor(null); loadAnos({ silent: true }); onChanged?.(); }}
      />

      <AlertDialog open={!!deletingAno} onOpenChange={(o) => !o && setDeletingAno(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Ano'yu sil?</AlertDialogTitle>
            <AlertDialogDescription>
              <span className="font-bold">Ano #{deletingAno?.number}</span> ({formatNumber(deletingAno?.length || 0)}m, {deletingAno?.type === "kapali" ? "Kapalı" : "U Kesit"}) silinecek. Bu işlem geri alınamaz.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>İptal</AlertDialogCancel>
            <AlertDialogAction className="bg-red-600 hover:bg-red-700" onClick={doDelete} data-testid="confirm-delete-ano">Sil</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
